import { Box, SimpleGrid } from '@chakra-ui/react';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { get_inRawOrEffluentSamplesString } from '@resistapp/client/utils/string-manipulation';
import { L1Targets, L2Targets } from '@resistapp/common/assays';
import { MinorTarget } from '@resistapp/common/assays-temp-96-gene-minor-targets';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { AbundanceMode, MetricMode, getOriginalEnvironmentId } from '@resistapp/common/types';
import { compact } from 'lodash';
import { RefObject, createRef, useMemo, useRef } from 'react';
import { GeneFocusTip } from '../tooltips/contextual-tips/gene-focus-tip';
import { AntibioticCompositionDetails } from './site-details/antibiotic-composition-details';
import { SiteKeyInsightsBox } from './site-details/key-insights-box';
import { MSTDetails } from './site-details/mst-details';
import { SiteDetailBarGraph } from './site-details/site-detail-bar-graph';
import { SiteDetailTreatedAndReducedBox } from './site-details/treated-and-reduced-box/site-detail-treated-and-reduced-box';

export function SiteDetailBoxes() {
  const { queryFilters } = useSampleDataContext();
  const { isAdmin } = useUser();
  const {
    metricMode,
    selectedSiteDatum,
    effectiveSiteProcessMode: effectiveSiteDetailsProcessMode,
    activeAbundanceMode,
    keyInsightsBySite,
  } = useOverviewContext();

  const containerRefs = useRef<Array<RefObject<HTMLDivElement>>>(
    Array.from({ length: 6 }, () => createRef<HTMLDivElement>()),
  );

  const siteInsights = useMemo(() => {
    if (metricMode === MetricMode.REDUCTION && selectedSiteDatum && keyInsightsBySite) {
      const originalEnvId = getOriginalEnvironmentId(selectedSiteDatum.environment);
      const result = originalEnvId ? keyInsightsBySite[originalEnvId] : undefined;
      return result;
    }
    return undefined;
  }, [metricMode, selectedSiteDatum, keyInsightsBySite]);

  const showInsightsBox = isAdmin && !!siteInsights;
  const antibioticOrReductionComp = useMemo(() => {
    const refIndex = showInsightsBox ? 1 : 0;

    if (metricMode === MetricMode.REDUCTION) {
      const reductionBox = (
        <SiteDetailTreatedAndReducedBox
          key="antibiotic-or-reduction-reduction"
          containerRef={containerRefs.current[refIndex]}
          groupingTarget={L1Targets.ARG}
          title="Antibiotic resistance gene reduction"
          explanation={`Average remaining and removed antibiotic resistance genes per L for each class of antibiotics`}
          // Height adjusted only when it spans two rows (insights hidden)
          height={showInsightsBox ? undefined : 640}
        />
      );
      if (!showInsightsBox) {
        return (
          <Box key="l2-target-stacked-reduction" gridRow="span 2" height="100%">
            {reductionBox}
          </Box>
        );
      }
      return reductionBox;
    } else {
      return (
        <AntibioticCompositionDetails
          key="antibiotic-or-reduction-argi-risk"
          containerRef={containerRefs.current[refIndex]}
          antibioticTarget={queryFilters.filters.selectedTargets}
        />
      );
    }
  }, [showInsightsBox, metricMode, queryFilters.filters.selectedTargets, containerRefs]);

  if (!selectedSiteDatum) {
    return null;
  }

  const isPerL = activeAbundanceMode === AbundanceMode.COPIES_PER_L;
  const perLitreMaybe = isPerL ? 'per\u00A0L' : '';

  const insightsComp = showInsightsBox ? (
    <SiteKeyInsightsBox
      key="key-insights"
      containerRef={containerRefs.current[0]}
      insights={siteInsights}
      id={TestSelectors.TEST_SITE_DETAIL_BOX_KEY_INSIGHTS}
    />
  ) : null;

  const pathogenComp =
    metricMode === MetricMode.REDUCTION ? (
      <SiteDetailTreatedAndReducedBox
        id={TestSelectors.TEST_SITE_DETAIL_BOX_PATHOGEN_MARKERS}
        key="pathogen-markers-details"
        containerRef={containerRefs.current[showInsightsBox ? 2 : 1]}
        groupingTarget={MinorTarget.PATHOGEN}
        title="Pathogen markers"
        explanation={`Independently detected pathogen marker genes per L
            ${get_inRawOrEffluentSamplesString(selectedSiteDatum, effectiveSiteDetailsProcessMode)}`}
      />
    ) : (
      <SiteDetailBarGraph
        id={TestSelectors.TEST_SITE_DETAIL_BOX_PATHOGEN_MARKERS}
        key="pathogen-bar"
        containerRef={containerRefs.current[showInsightsBox ? 2 : 1]}
        target={MinorTarget.PATHOGEN}
        title="Pathogen markers"
        noGenesText="No pathogen associated genes detected"
        italic={true}
        customExplanation={({ inRawOrEffluentSamples }) => (
          <span>
            Copy numbers of independently detected pathogen marker genes {perLitreMaybe}
            {inRawOrEffluentSamples ? inRawOrEffluentSamples : ''}
          </span>
        )}
      />
    );

  // Determine the ref corresponding to the pathogen component (used for anchoring the contextual tip)
  const pathogenRef = containerRefs.current[showInsightsBox ? 2 : 1];

  const pollutionComp =
    metricMode === MetricMode.REDUCTION ? (
      <SiteDetailTreatedAndReducedBox
        key="pollution-markers-details"
        containerRef={containerRefs.current[showInsightsBox ? 3 : 2]}
        groupingTarget={MinorTarget.POLLUTION_MARKER}
        title="Pollution markers"
        explanation={`Copy numbers (per L) of genes associated with AMR pollution
          ${get_inRawOrEffluentSamplesString(selectedSiteDatum, effectiveSiteDetailsProcessMode)}`}
      />
    ) : (
      <SiteDetailBarGraph
        key="pollution-bar"
        id="site-detail-box-pollution"
        containerRef={containerRefs.current[showInsightsBox ? 3 : 2]}
        target={MinorTarget.POLLUTION_MARKER}
        title="Pollution markers"
        titleExplanation={
          <span>
            Copy numbers {isPerL ? '(per L)' : ''} of genes associated with AMR pollution
            {get_inRawOrEffluentSamplesString(selectedSiteDatum, effectiveSiteDetailsProcessMode)}
          </span>
        }
        noGenesText="No pollution markers detected"
      />
    );

  const mgeComp =
    metricMode === MetricMode.REDUCTION ? (
      <SiteDetailTreatedAndReducedBox
        key="mge-treated-reduced"
        containerRef={containerRefs.current[showInsightsBox ? 4 : 3]}
        groupingTarget={MinorTarget.MGE}
        title="Mobile genetic elements"
        explanation={`Copy numbers (per L) of genes associated with MGEs
          ${get_inRawOrEffluentSamplesString(selectedSiteDatum, effectiveSiteDetailsProcessMode)}`}
      />
    ) : (
      <SiteDetailBarGraph
        key="mge-details"
        id="site-detail-box-mge"
        containerRef={containerRefs.current[showInsightsBox ? 4 : 3]}
        target={L2Targets.MGE}
        title="Mobile genetic elements"
        titleExplanation={
          <span>
            Copy numbers {isPerL ? '(per L)' : ''} of genes associated with MGEs{' '}
            {get_inRawOrEffluentSamplesString(selectedSiteDatum, effectiveSiteDetailsProcessMode)}
          </span>
        }
        noGenesText="No pollution markers detected"
      />
    );

  const mstComp = <MSTDetails key="mst-details" containerRef={containerRefs.current[showInsightsBox ? 5 : 4]} />;

  const components = compact([insightsComp, antibioticOrReductionComp, pathogenComp, pollutionComp, mgeComp, mstComp]);

  return (
    <SimpleGrid columns={{ base: 1, lg: 2, '2xl': 3 }} gap={8} className={TestSelectors.TEST_SITE_DETAIL_BOXES}>
      {components}
      {/* Contextual tip for focusing on gene/antibiotic - rendered once within the Site Detail Boxes */}
      <GeneFocusTip containerRef={pathogenRef} />
    </SimpleGrid>
  );
}
